/**
 * Firebase Auth for the "Continue with Google" button.
 *
 * The popup returns a Firebase ID token that is exchanged for a Flexv Coach
 * session by `POST /coach-auth/google`. The API verifies the token against
 * Google's public certificates, so no service-account secret lives in the
 * browser — only the public web config below (same project as the Portal).
 *
 * Both Firebase modules are imported dynamically: the SDK is ~300 kB, and
 * most coaches still sign in with email + password.
 */
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/** Hides the Google button when the env config is missing. */
export const isGoogleSignInEnabled = Boolean(
    firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId,
);

/** What the popup gave us — the API still verifies `idToken` server-side. */
export interface GoogleCredential {
    idToken: string;
    email: string | null;
    fullName: string | null;
    avatarUrl: string | null;
}

/** Opens the Google account chooser and resolves with the Firebase ID token. */
export async function signInWithGoogle(): Promise<GoogleCredential> {
    if (!isGoogleSignInEnabled) {
        throw new Error("Google Sign-In is not configured");
    }

    const [{ getApp, getApps, initializeApp }, authModule] = await Promise.all([
        import("firebase/app"),
        import("firebase/auth"),
    ]);
    const { GoogleAuthProvider, getAuth, signInWithPopup, signOut } = authModule;

    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    const auth = getAuth(app);

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    const popup = await signInWithPopup(auth, provider);
    const idToken = await popup.user.getIdToken();

    const credential: GoogleCredential = {
        idToken,
        email: popup.user.email,
        fullName: popup.user.displayName,
        avatarUrl: popup.user.photoURL,
    };

    // Our API session is the source of truth; don't keep a second session alive.
    await signOut(auth).catch(() => undefined);

    return credential;
}
